import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  MenuItem,
  Box,
} from '@mui/material';
import ScacTypeService from '../Services/ScacType';
import loaderGif from "./../Assests/loader.gif";

const SCACFormDialog = ({ open, handleClose, handleSave, initialData }) => {
  const initialFormData = {
    scac_code: '',
    scac_name: '',
    scac_type_id: '',
    phone: '',
    url: '',
  };

  const [formData, setFormData] = useState(initialFormData);
  const [scacTypeOptions, setScacTypeOptions] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isLoading, setIsLoading] = useState (false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading (true);
        const scacTypeData = await ScacTypeService.fetchScacType();
        setScacTypeOptions(
          Array.isArray(scacTypeData.data) ? scacTypeData.data : []
        );
      } catch (error) {
        console.error('Error fetching SCAC types:', error);
        setScacTypeOptions([]);
      }
      finally {
        setIsLoading (false);
      }
    };

    if (open) fetchData();
  }, [open]);

  useEffect(() => {
    setFieldErrors({});
    setFormData(initialData || initialFormData);
  }, [initialData, open]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = () => {
    const errors = {};

    // SCAC Code validation
    if (!formData.scac_code) {
        errors.scac_code = 'SCAC Code is required';
    } else if (typeof formData.scac_code !== 'string') {
        errors.scac_code = 'SCAC Code must be a string';
    } 

    // SCAC Name validation
    if (!formData.scac_name) {
        errors.scac_name = 'SCAC Name is required';
    } else if (typeof formData.scac_name !== 'string') {
        errors.scac_name = 'SCAC Name must be a string';
    }

    // SCAC Type ID validation
    if (!formData.scac_type_id) {
        errors.scac_type_id = 'SCAC Type is required';
    } else if (!scacTypeOptions.some((option) => option.scac_type_id === formData.scac_type_id)) {
        errors.scac_type_id = 'SCAC Type ID must be a valid option';
    }

    // Phone validation (only if provided and not "N/A")
    if (formData.phone !== 'N/A' && formData.phone && !/^(\d{10}|\d{3}-\d{3}-\d{4}|\d{2}-\d{2}-\d{2}-\d{2}-\d{2})$/.test(formData.phone)) {
        errors.phone = 'Phone should be in the format 8765432190, XXX-XXXX-XXXX, or XX-XX-XX-XX-XX';
    }

    // URL validation (only if provided and not "N/A")
    if (formData.url !== 'N/A' && formData.url && !/^https?:\/\/[\w.-]+(\.[\w.-]+)+(:\d+)?(\/[^\s]*)?$/.test(formData.url)) {
        errors.url = 'URL should be a valid URL starting with http:// or https://';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0; // Returns true if no errors
};

const onSave = async () => {
  const updatedFormData = {
    ...formData,
    phone: formData.phone || 'N/A',
    url: formData.url || 'N/A',
  };

  if (validateForm(updatedFormData)) {
    setIsSaving(true);
    try {
      setIsLoading(true); 
      await handleSave(updatedFormData); 
      handleClose(); 
    } catch (error) {
      console.error("Error saving SCAC:", error);
    } finally {
      setIsLoading(false);
      setIsSaving(false);
 
    }
  }
};

  
  

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{initialData ? 'Update SCAC Record' : 'Add New SCAC Record'}</DialogTitle>
      {isSaving && (
        <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="rgba(255, 255, 255, 0.8)"
        zIndex={1}
      >
        <img src={loaderGif} alt="Loading..." width={50} height={50} />
      </Box>
      )}
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          name="scac_code"
          label="SCAC *"
          fullWidth
          variant="outlined"
          value={formData.scac_code}
          onChange={handleChange}
        />
        {fieldErrors.scac_code && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.scac_code}</div>}
        <TextField
          margin="dense"
          name="scac_name"
          label="SCAC Name *"
          fullWidth
          variant="outlined"
          value={formData.scac_name}
          onChange={handleChange}

        />
        {fieldErrors.scac_name && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.scac_name}</div>}

        <TextField
          margin="dense"
          name="scac_type_id"
          label="Type *"
          select
          fullWidth
          variant="outlined"
          value={formData.scac_type_id || ''}
          onChange={handleChange}
        >
          {scacTypeOptions.map(option => (
            <MenuItem key={option.scac_type_id} value={option.scac_type_id}>
              {option.description}
            </MenuItem>
          ))}
        </TextField>
        {fieldErrors.scac_type_id && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.scac_type_id}</div>}

        <TextField
          margin="dense"
          name="phone"
          label="Phone"
          fullWidth
          variant="outlined"
          value={formData.phone}
          onChange={handleChange}
        />
        {fieldErrors.phone && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.phone}</div>}

        <TextField
          margin="dense"
          name="url"
          label="URL"
          fullWidth
          variant="outlined"
          value={formData.url}
          onChange={handleChange}
        
        />
        {fieldErrors.url && <div style={{ color: 'red', fontSize: '0.875rem' }}>{fieldErrors.url}</div>}

      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={onSave} color="primary" variant="contained">
          {initialData ? 'Update' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SCACFormDialog;
